import type { Lead, Answer, ScoringResult, MarketComparison, FinancialProjection, LeadCaptureData } from './types';
import type { ProductConfig } from './types/product';
import { getOportunidadeIdFromUrl } from './utils/url-parser';
import { getProductFromPath } from './config/products/index';
import { leadService } from './services/crm/lead-service';
import { interactionService } from './services/crm/interaction-service';
import { pdfStorageService } from './services/pdf/pdf-storage-service';
import { sendDiagnosticEmail } from './services/email/email-service';
import { trackClientEvent } from './services/telemetry/client-event-service';
import { ScoringEngine } from './services/scoring/scoring-engine';
import { BenchmarkService } from './services/market-data/benchmark-service';
import { enqueueFinalize, reliableDeliveryEnabled } from './services/delivery/reliable-delivery';
import { WelcomeScreen } from './ui/screens/WelcomeScreen';
import { LeadCaptureScreen } from './ui/screens/LeadCaptureScreen';
import { ChatScreen } from './ui/screens/ChatScreen';
import { ResultScreen } from './ui/screens/ResultScreen';
import './ui/styles/main.css';

export class App {
  private appElement: HTMLElement;
  private currentScreenComponent: WelcomeScreen | LeadCaptureScreen | ChatScreen | ResultScreen | null = null;
  private productConfig: ProductConfig;

  private lead: Lead | null = null;
  private leadCreationPromise: Promise<Lead> | null = null;
  private answers: Answer[] = [];
  private scoringResult: ScoringResult | null = null;
  private marketComparison: MarketComparison[] = [];
  private financialProjection: FinancialProjection | null = null;
  private diagnosticSessionId = this.generateSessionId();

  constructor() {
    this.appElement = document.getElementById('app') as HTMLElement;
    if (!this.appElement) {
      throw new Error('App element not found');
    }
    this.productConfig = getProductFromPath();
  }

  async init(): Promise<void> {
    const oportunidadeId = getOportunidadeIdFromUrl();

    if (oportunidadeId) {
      await this.handleExistingLead(oportunidadeId);
    } else {
      this.showWelcomeScreen();
    }
  }

  private async handleExistingLead(oportunidadeId: number): Promise<void> {
    try {
      this.showLoading('Carregando dados...');

      const lead = await leadService.getLeadById(oportunidadeId, this.productConfig.funilId);

      if (lead) {
        this.lead = lead;
        this.showChatScreen();
      } else {
        console.warn('Lead not found, showing welcome screen');
        this.showWelcomeScreen();
      }
    } catch (error) {
      console.error('Error loading lead:', error);
      this.showWelcomeScreen();
    }
  }

  private showLoading(message: string): void {
    this.clearScreen();
    this.appElement.innerHTML = `
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        gap: 20px;
      ">
        <div class="loading-spinner"></div>
        <p style="color: #64748b;">${message}</p>
      </div>
    `;
  }

  private showWelcomeScreen(): void {
    this.clearScreen();

    this.currentScreenComponent = new WelcomeScreen(this.appElement, {
      title: this.productConfig.title,
      description: this.productConfig.description,
      onStart: () => this.showLeadCaptureScreen()
    });
  }

  private showLeadCaptureScreen(): void {
    this.clearScreen();

    this.currentScreenComponent = new LeadCaptureScreen(this.appElement, {
      onSubmit: (data) => this.handleLeadCapture(data)
    });
  }

  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `diag-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private async handleLeadCapture(data: LeadCaptureData): Promise<void> {
    this.diagnosticSessionId = this.generateSessionId();
    // Create a temporary lead immediately so the user can proceed to chat without waiting for CRM
    this.lead = {
      id: 0,
      nome: data.nome,
      email: data.email,
      telefone: data.telefone,
      cidade: data.cidade,
      origem: this.productConfig.crmOrigem,
      campanha: 'Diagnostico',
      funilId: this.productConfig.funilId,
      honeypot: data.honeypot || '',
      sessionId: this.diagnosticSessionId
    };

    await trackClientEvent({
      sessionId: this.diagnosticSessionId,
      eventType: 'lead_capture_submitted',
      leadName: data.nome,
      message: this.productConfig.id
    });

    this.leadCreationPromise = leadService.createLead(data, this.productConfig).then(lead => {
      this.lead = {
        ...lead,
        honeypot: this.lead?.honeypot || '',
        sessionId: this.diagnosticSessionId
      };
      void trackClientEvent({
        sessionId: this.diagnosticSessionId,
        eventType: 'crm_lead_created',
        leadName: lead.nome,
        message: String(lead.id || '')
      });
      return this.lead;
    }).catch(error => {
      console.error('Error creating lead in CRM (non-blocking):', error);
      void trackClientEvent({
        sessionId: this.diagnosticSessionId,
        eventType: 'crm_lead_failed',
        leadName: this.lead?.nome || data.nome,
        message: error instanceof Error ? error.message.slice(0, 500) : 'crm_error'
      });
      return this.lead!;
    });

    this.showChatScreen();
  }

  private showChatScreen(): void {
    if (!this.lead) {
      console.error('No lead data available');
      this.showWelcomeScreen();
      return;
    }

    this.clearScreen();

    this.currentScreenComponent = new ChatScreen(this.appElement, {
      lead: this.lead,
      questions: this.productConfig.questions,
      onComplete: (answers) => this.handleQuestionnaireComplete(answers)
    });
  }

  private computePaybackAnswer(answers: Answer[]): Answer | null {
    const grossRevenue = answers.find(a => a.questionId === 'fin_gross_revenue')?.value as number || 0;
    const netRevenue = answers.find(a => a.questionId === 'fin_net_revenue')?.value as number || 0;
    const investment = answers.find(a => a.questionId === 'fin_investment')?.value as number || 0;
    if (grossRevenue <= 0 || investment <= 0) return null;

    const monthlyNetProfit = netRevenue > 0 ? netRevenue : grossRevenue * 0.55;
    const paybackMonths = Math.round(investment / monthlyNetProfit);
    const BASE = 18;

    let score: number;
    if (paybackMonths <= BASE) score = 10;
    else if (paybackMonths <= BASE + 6) score = 8;
    else if (paybackMonths <= BASE + 12) score = 7;
    else if (paybackMonths <= BASE + 24) score = 6;
    else if (paybackMonths <= BASE + 36) score = 4;
    else score = 2;

    return { questionId: 'fin_payback', value: paybackMonths, score, timestamp: new Date() };
  }

  private async handleQuestionnaireComplete(answers: Answer[]): Promise<void> {
    if (this.productConfig.id === 'franqueabilidade') {
      const paybackAnswer = this.computePaybackAnswer(answers);
      if (paybackAnswer) answers = [...answers, paybackAnswer];
    }

    this.answers = answers;

    const scoringEngine = new ScoringEngine(answers, this.productConfig.scoring);
    this.scoringResult = scoringEngine.calculate();

    if (this.productConfig.id === 'franqueabilidade') {
      const benchmarkService = new BenchmarkService(answers);
      this.marketComparison = benchmarkService.generateComparison();
      this.financialProjection = benchmarkService.generateFinancialProjection();
    } else {
      this.marketComparison = [];
      this.financialProjection = null;
    }

    await this.delay(1500);

    void trackClientEvent({
      sessionId: this.diagnosticSessionId,
      eventType: 'questionnaire_completed',
      leadName: this.lead?.nome || null,
      scorePercentage: this.scoringResult.totalPercentage,
      message: this.productConfig.id
    });

    this.showResultScreen();
  }

  private showResultScreen(): void {
    if (!this.lead || !this.scoringResult) {
      console.error('Missing data for result screen');
      return;
    }

    this.clearScreen();

    this.currentScreenComponent = new ResultScreen(this.appElement, {
      lead: this.lead,
      scoring: this.scoringResult,
      marketComparison: this.marketComparison,
      financialProjection: this.financialProjection,
      answers: this.answers,
      productConfig: this.productConfig,
      onPdfReady: (blob) => this.handlePdfReady(blob)
    });
  }

  private async handlePdfReady(blob: Blob): Promise<void> {
    if (!this.lead || !this.scoringResult) return;

    console.log('[App] PDF ready, awaiting CRM lead ID then uploading...');

    void trackClientEvent({
      sessionId: this.diagnosticSessionId,
      eventType: 'pdf_ready',
      leadName: this.lead.nome,
      scorePercentage: this.scoringResult.totalPercentage
    });

    if (reliableDeliveryEnabled()) {
      try {
        const result = await enqueueFinalize({
          sessionId: this.diagnosticSessionId,
          productId: this.productConfig.id,
          leadId: this.lead.id || null,
          funilId: this.productConfig.funilId,
          origem: this.productConfig.crmOrigem,
          lead: {
            nome: this.lead.nome,
            email: this.lead.email,
            telefone: this.lead.telefone,
            cidade: this.lead.cidade,
            campanha: this.lead.campanha,
            origem: this.lead.origem,
            honeypot: this.lead.honeypot
          },
          scoring: {
            totalScore: this.scoringResult.totalScore,
            totalPercentage: this.scoringResult.totalPercentage,
            finalRecommendation: this.scoringResult.finalRecommendation,
            categoryScores: this.scoringResult.categoryScores
          },
          product: {
            id: this.productConfig.id,
            title: this.productConfig.title,
            funilId: this.productConfig.funilId,
            crmOrigem: this.productConfig.crmOrigem
          },
          answers: this.answers,
          pdfBlob: blob
        });

        if (result.ok) {
          void trackClientEvent({
            sessionId: this.diagnosticSessionId,
            eventType: 'reliable_enqueue_ok',
            leadName: this.lead.nome,
            scorePercentage: this.scoringResult.totalPercentage,
            message: String(result.jobId || '')
          });
          return;
        }
        void trackClientEvent({
          sessionId: this.diagnosticSessionId,
          eventType: 'reliable_enqueue_failed',
          leadName: this.lead.nome,
          scorePercentage: this.scoringResult.totalPercentage,
          message: 'enqueue_not_ok'
        });
      } catch (error) {
        void trackClientEvent({
          sessionId: this.diagnosticSessionId,
          eventType: 'reliable_enqueue_failed',
          leadName: this.lead.nome,
          scorePercentage: this.scoringResult.totalPercentage,
          message: error instanceof Error ? error.message.slice(0, 500) : 'enqueue_failed'
        });
      }

      try {
        await sendDiagnosticEmail(this.lead, this.scoringResult, this.productConfig, blob);
      } catch (error) {
        console.error('[App] Error sending diagnostic email (non-blocking):', error);
      }
      return;
    }

    // Ensure the background CRM creation has completed before uploading
    if (this.leadCreationPromise) {
      await this.leadCreationPromise;
    }

    if (!this.lead.id) {
      console.warn('[App] Lead ID not available, skipping CRM upload');
    } else {
      try {
        await pdfStorageService.uploadPdfToCrm(blob, this.lead.id);
        console.log('[App] PDF uploaded successfully');

        await interactionService.createDiagnosticCompletedInteraction(
          this.lead.id,
          this.scoringResult.totalPercentage,
          this.scoringResult.finalRecommendation
        );
        console.log('[App] Interaction created successfully');
      } catch (error) {
        console.error('[App] Error uploading PDF or creating CRM interaction:', error);
      }
    }

    try {
      await sendDiagnosticEmail(this.lead, this.scoringResult, this.productConfig, blob);
    } catch (error) {
      console.error('[App] Error sending diagnostic email (non-blocking):', error);
    }
  }

  private clearScreen(): void {
    if (this.currentScreenComponent && 'remove' in this.currentScreenComponent) {
      this.currentScreenComponent.remove();
    }
    this.currentScreenComponent = null;
    this.appElement.innerHTML = '';
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
